import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const GlobalEcosystem = () => {
  const devices = [
    {
      icon: FiIcons.FiSmartphone,
      color: "bg-[#ef4444]", // Red
      title: "62% Mobile Share",
      subtitle: "85% Video Odds",
      desc: "Videos dominate the first screen on mobile search."
    },
    {
      icon: FiIcons.FiMonitor,
      color: "bg-[#3b82f6]", // Blue
      title: "35% Desktop Share",
      subtitle: "55% Video Odds",
      desc: "Best for deep-dive reviews and tutorials."
    },
    {
      icon: FiIcons.FiTablet,
      color: "bg-[#84cc16]", // Lime Green
      title: "3% Tablet Share",
      subtitle: "60% Video Odds",
      desc: "High conversion for leisure research."
    }
  ];

  const browsers = [
    { name: "Chrome (65.2%)", status: "Maximum (Native)" },
    { name: "Safari (18.5%)", status: "Very High (iOS Carousel Priority)" },
    { name: "Edge (5.4%)", status: "High (Chromium Core Support)" },
    { name: "Firefox (2.3%)", status: "Med-High (Standard SEO Compliance)" },
    { name: "Opera / Brave (6.0%)", status: "High (Privacy user adoption)" }
  ];

  const regions = [
    { name: "USA / Canada", status: "Ultra-Fast (iOS / Chrome Desktop)" },
    { name: "Europe (UK/DE/FR)", status: "High Speed (Android / Firefox)" },
    { name: "Southeast Asia", status: "5G Optimized (Mobile Chrome / Android)" },
    { name: "Global (Rest of World)", status: "Variable (All Ecosystems)" }
  ];

  return (
    <section className="relative py-24 bg-[#0B0F19] overflow-hidden border-b border-white/5">
      <div className="max-w-5xl mx-auto px-6 relative z-10">
        
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight"
          >
            Global Ecosystem Dominance
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-sm md:text-[15px] text-slate-300 max-w-3xl mx-auto leading-relaxed font-medium"
          >
            "We secure digital real estate where your audience actually spends their time. Our video assets are optimized for global bandwidth, devices, browsers, and regional intent."
          </motion.p>
        </div>

        {/* Top Row: Devices */}
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          {devices.map((device, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="glass-card p-8 rounded-2xl border-white/5 bg-[#12151e] hover:border-white/10 transition-colors"
            >
              <div className={`w-12 h-12 rounded-xl ${device.color} flex items-center justify-center mb-8 shadow-lg`}>
                <SafeIcon icon={device.icon} className="text-2xl text-white" strokeWidth={2.5} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{device.title}</h3>
              <p className="text-sm font-bold text-slate-400 mb-6">{device.subtitle}</p>
              <p className="text-sm text-slate-400 leading-relaxed font-medium">
                {device.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Middle Row: Browser & Regional Intent */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Browser Ecosystem Support */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="glass-card p-8 rounded-2xl border-white/5 bg-[#12151e]"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-xl bg-[#3b82f6] flex items-center justify-center shadow-lg">
                <SafeIcon icon={FiIcons.FiLayout} className="text-2xl text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white leading-tight">Browser Ecosystem Support</h3>
                <p className="text-xs text-slate-400 font-medium">Global Search Exposure Matrix</p>
              </div>
            </div>

            <div className="space-y-0">
              {browsers.map((item, idx) => (
                <div key={idx} className={`flex justify-between items-center py-4 ${idx !== browsers.length - 1 ? 'border-b border-white/5' : ''}`}>
                  <span className="text-sm font-bold text-white">{item.name}</span>
                  <span className="text-[13px] font-bold text-[#60a5fa]">{item.status}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Regional Country Intent */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="glass-card p-8 rounded-2xl border-white/5 bg-[#12151e]"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-xl bg-[#ef4444] flex items-center justify-center shadow-lg">
                <SafeIcon icon={FiIcons.FiGlobe} className="text-2xl text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white leading-tight">Regional Country Intent</h3>
                <p className="text-xs text-slate-400 font-medium">Global Optimization Ranking</p>
              </div>
            </div>

            <div className="space-y-0">
              {regions.map((item, idx) => (
                <div key={idx} className={`flex justify-between items-center py-5 ${idx !== regions.length - 1 ? 'border-b border-white/5' : ''}`}>
                  <span className="text-sm font-bold text-white">{item.name}</span>
                  <span className="text-[13px] font-bold text-[#f87171]">{item.status}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom Row: Scale Share */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="glass-card p-10 rounded-2xl border-white/5 bg-[#12151e] text-center flex flex-col items-center"
        >
          <div className="w-12 h-12 rounded-xl bg-[#84cc16] flex items-center justify-center mb-6 shadow-lg">
            <SafeIcon icon={FiIcons.FiTrendingUp} className="text-2xl text-[#0B0F19]" strokeWidth={3} />
          </div>
          <h3 className="text-xl font-bold text-white mb-4">Big Possibility Scale Share</h3>
          <p className="text-sm md:text-[15px] text-slate-300 max-w-3xl mx-auto leading-relaxed font-medium">
            "Unlike text content, video SEO assets are natively cross-indexed across 180+ countries. A single asset can rank in Japan, USA, and Brazil simultaneously with zero extra cost."
          </p>
        </motion.div>

      </div>
    </section>
  );
};

export default GlobalEcosystem;